/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-23 19:58:17
 *
 *******************************************************/
/// @flow

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Modal, Card, FormLayout, Layout } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import _ from 'lodash';
import { reduxForm, getFormValues, reset, Field } from 'redux-form';
import { buildInitialRule, buildRule, validateRule } from 'src/helpers/merge-values-helper';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import RenderAutocompleteV2 from 'src/components/views/common/autocomplete-hoc-v2';
import { getShop } from 'src/helpers';
import { getValuesOfType } from 'src/helpers/merge-values-helper';
import { routes } from 'src/routes';
import history from 'src/routes/history';

import * as MergeValuesActions from 'actions/merge-values-actions';

import type { MergeValuesRuleType } from 'src/models/merge-values';

type Props = {
  active: boolean,
  handleChange: () => void,
  saveValue: (data: Object) => void,
  index: number,
  rules: Object,
  type: string
};

type ComposedProps = Props & WithI18nProps;

type State = {
  options: Array<Object>,
  currentIndex: ?number,
  excludedValues: Array<Object>
};

class RuleModal extends Component<ComposedProps, State> {
  state = { options: [], currentIndex: undefined, excludedValues: [] };

  componentWillReceiveProps(nextProps) {
    const { active } = this.props;
    const {
      active: nextActive,
      index: nextIndex,
      rules: nextRules,
      valuesOfType: nextValuesOfType
    } = nextProps;

    const { currentIndex } = this.state;
    if (!active && nextActive && nextIndex !== currentIndex) {
      this.removeExistOption(nextRules, nextValuesOfType, nextIndex);
      // Update current index
      this.setState({ currentIndex: nextIndex });
    }
  }

  render() {
    const { active, handleSubmit, pristine, index, i18n, fromValues, updating, type } = this.props;
    const { excludedValues } = this.state;

    const title =
      index !== -1
        ? i18n.translate('AdminHTML.MergeValues.RuleModal.titleEdit')
        : i18n.translate('AdminHTML.MergeValues.RuleModal.titleAdd');
    return (
      <Modal
        open={active}
        onClose={this.handleChange}
        title={title}
        primaryAction={{
          content: i18n.translate('AdminHTML.Common.Action.save'),
          onAction: handleSubmit(this.handleSave.bind(this)),
          disabled: pristine,
          loading: updating
        }}
        secondaryActions={[
          {
            content: i18n.translate('AdminHTML.Common.Action.cancel'),
            onAction: this.handleChange
          }
        ]}>
        <div className="BC-Modal__OptionFilter">
          <Layout>
            <Layout.Section>
              <Card>
                <Card.Section>
                  <form onSubmit={this.handleDefaultSubmit}>
                    <FormLayout>
                      <Field
                        name="toValue"
                        label={i18n.translate('AdminHTML.MergeValues.RuleModal.mergedValues_Label')}
                        tooltip={i18n.translate(
                          'AdminHTML.MergeValues.RuleModal.mergedValues_Tooltip'
                        )}
                        component={RenderTextField}
                      />
                      {
                        <Field
                          name={`fromValues`}
                          component={RenderAutocompleteV2}
                          type={this.getFilterType(type)}
                          filterPrefix={this.getFilterPrefix(type)}
                          value={fromValues}
                          placeholder={i18n.translate(
                            'AdminHTML.Common.Placeholder.renderAutocomplete'
                          )}
                          label={i18n.translate('AdminHTML.MergeValues.RuleModal.fromValues_Label')}
                          tooltip={i18n.translate(
                            'AdminHTML.MergeValues.RuleModal.fromValues_Tooltip'
                          )}
                          excludedValues={excludedValues}
                        />
                      }
                    </FormLayout>
                  </form>
                </Card.Section>
              </Card>
            </Layout.Section>
          </Layout>
        </div>
      </Modal>
    );
  }

  // Handle default form submit
  handleDefaultSubmit(e) {
    e.preventDefault();
  }

  //Handle close modal
  handleChange = () => {
    const { handleChange, resetForm } = this.props;

    resetForm();
    handleChange();
  };

  // Get prefix for rendering autocomplete v2
  getFilterPrefix = optionType => {
    if (['vendor', 'product_type', 'tag'].includes(optionType)) return '';

    // Filter option
    const optionName = optionType ? optionType.replace('opt_', '') : optionType;
    const filterPrefix = `${optionName}:`;

    return filterPrefix;
  };

  // Get filter type for rendering autocomplete v2
  getFilterType = optionType => {
    if (['vendor', 'product_type', 'tag'].includes(optionType)) return `${optionType}s`;

    if (_.startsWith(optionType, 'opt_')) return 'options';

    return '';
  };

  // Remove exit option null
  removeExistOption(rules, valuesOfType, index) {
    let result = _.cloneDeep(valuesOfType);
    const shop = getShop();
    let excludedValues = [];

    if (shop !== 'fortyfiveten.myshopify.com') {
      rules.forEach((rule, key) => {
        if (index !== key && !_.isEmpty(rule.fromValues)) {
          rule.fromValues.forEach(item => {
            _.remove(result, i => i.value === item);
            excludedValues.push({ value: item, label: item });
          });
        }
      });
    }
    this.setState({ options: result, excludedValues });
  }

  // Handle click save button
  handleSave = async data => {
    const { saveRule, index, i18n, rules, type } = this.props;
    if (validateRule(data, index, rules, i18n)) {
      let rule = buildRule(data);
      const result = await saveRule(rule);
      if (result.type === 'SAVE_MERGE_VALUES_RULE_SUCCESS') {
        this.handleChange();

        // Reset current index
        if (index === -1) {
          this.setState({ currentIndex: undefined });
          history.push(routes.mergeValues.edit(type));
        }
      }
    }
  };
}

const RuleModalForm = reduxForm({
  form: 'RuleModalForm', // a unique identifier for this form
  enableReinitialize: true
})(RuleModal);

const mapStateToProps = (state, props) => {
  const ruleModalForm = getFormValues('RuleModalForm')(state);
  const valuesOfType = getValuesOfType(props.type, state);
  const { rules, index } = props;
  const rule = index === -1 ? {} : rules[index];
  const initialValues = buildInitialRule(rule, props.type);

  return {
    initialValues,
    fromValues: _.get(ruleModalForm, 'fromValues', []),
    valuesOfType,
    updating: _.get(state.mergeValues, 'updating', false)
  };
};

const mapDispatchToProps = (dispatch, props) => ({
  resetForm: () => dispatch(reset('RuleModalForm')),
  saveRule: (rule: MergeValuesRuleType) =>
    dispatch(MergeValuesActions.saveMergeValuesRule(rule, props.i18n))
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(RuleModalForm));
